import React, { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DropdownSelect from "../common/DropdownSelect";
import "../../styles/admin/TeacherAssignForm.scss";

/**
 * TeacherSelectForm — pick teachers from a not-yet-assigned list,
 * and optionally assign one or more subjects to each selected teacher.
 *
 * Props:
 *   teachers      — list of teacher objects { id, name, email }
 *   subjects      — list of subject objects { id, name } (optional)
 *   onSubmit      — called with { teacherIds: string[], subject_ids: string[] }
 *   getSubjectsByCourse — optional fn (courseKey) => void, if course selector is needed
 */
const TeacherSelectForm = ({
  isOpen,
  onClose,
  onSubmit,
  teachers = [],
  subjects = [],        // list of { id, name } for the current batch's course
  title = "Add Teachers",
}) => {
  const [selected, setSelected] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [errors, setErrors] = useState({});

  const teacherOptions = useMemo(
    () =>
      (teachers || []).map((t) => ({
        key: t?.id ?? t?._id ?? t?.value ?? t,
        value: t?.id ?? t?._id ?? t?.value ?? t,
        label:
          t?.name && t?.email
            ? `${t.name} (${t.email})`
            : t?.name || t?.label || String(t),
      })),
    [teachers]
  );

  const subjectOptions = useMemo(
    () =>
      (subjects || []).map((s) => ({
        value: s?.id ?? s?._id ?? s?.value ?? s,
        label: s?.name ?? s?.label ?? String(s),
      })),
    [subjects]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!selected || selected.length === 0) {
      newErrors.teachers = "Please select at least one teacher";
    }
    if (subjectOptions.length > 0 && selectedSubjects.length === 0) {
      newErrors.subjects = "Please select at least one subject";
    }
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    const payload = {
      teacherIds: selected?.map((c) => c.key),
      subject_ids: selectedSubjects.map((s) => s.value),
    };
    onSubmit && onSubmit(payload);
    setSelected([]);
    setSelectedSubjects([]);
  };

  const handleClose = () => {
    onClose();
    setSelected([]);
    setSelectedSubjects([]);
    setErrors({});
  };

  return (
    <div className="assign-form-overlay">
      <div className="assign-form-container">
        <div className="assign-form-header">
          <h2>{title}</h2>
          <button
            className="assign-close-button"
            onClick={handleClose}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <form className="assign-form" onSubmit={handleSubmit}>
          {/* Teachers multi-select */}
          <div className={`assign-form-group ${errors.teachers ? "has-error" : ""}`}>
            <label htmlFor="teachers-select">Teachers</label>
            <DropdownSelect
              inputId="teachers-select"
              isMulti
              options={teacherOptions}
              value={selected}
              onChange={(vals) => {
                setSelected(vals || []);
                if (vals && vals.length > 0) {
                  setErrors((prev) => ({ ...prev, teachers: null }));
                }
              }}
              placeholder="Select teachers"
              searchable
            />
            {errors.teachers && (
              <span className="assign-error-message">{errors.teachers}</span>
            )}
          </div>

          {/* Subjects multi-select (shown only when subjects list is provided) */}
          {subjectOptions.length > 0 && (
            <div className={`assign-form-group ${errors.subjects ? "has-error" : ""}`}>
              <label htmlFor="subjects-select-picker">
                Subjects{" "}
                <span
                  style={{
                    fontWeight: 400,
                    color: "var(--color-text-muted, #888)",
                    fontSize: "0.85em",
                  }}
                >
                  (select one or more)
                </span>
              </label>
              <DropdownSelect
                inputId="subjects-select-picker"
                isMulti
                options={subjectOptions}
                value={selectedSubjects}
                onChange={(vals) => {
                  setSelectedSubjects(vals || []);
                  if (vals && vals.length > 0) {
                    setErrors((prev) => ({ ...prev, subjects: null }));
                  }
                }}
                placeholder="Select subjects…"
                searchable
              />
              {errors.subjects && (
                <span className="assign-error-message">{errors.subjects}</span>
              )}
              {selectedSubjects.length > 0 && (
                <span
                  style={{
                    fontSize: "0.8em",
                    color: "var(--color-text-muted, #888)",
                    marginTop: 4,
                    display: "block",
                  }}
                >
                  {selectedSubjects.length} subject
                  {selectedSubjects.length !== 1 ? "s" : ""} selected
                </span>
              )}
            </div>
          )}

          <div className="assign-form-actions">
            <button
              type="button"
              className="assign-cancel-button"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="assign-submit-button btn-interactive"
            >
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherSelectForm;
